import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { featuredProducts } from '../../../mocks/products';

export default function ProductGrid() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) ref.current?.classList.add('visible'); }, { threshold: 0.1 });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);

  return (
    <section className="py-28 bg-cream-mid">
      <div ref={ref} className="fade-up max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-4">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-gold" />
              <span className="text-gold text-xs font-semibold uppercase tracking-[0.25em]">Featured Pieces</span>
            </div>
            <h2 className="font-serif text-5xl md:text-6xl font-bold text-dark leading-tight">
              Handpicked<br />For You
            </h2>
          </div>
          <Link
            to="/collections"
            className="inline-flex items-center gap-2 text-dark/60 text-sm font-medium hover:text-gold transition-colors cursor-pointer whitespace-nowrap self-start md:self-auto underline underline-offset-4"
          >
            Browse All Products
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {featuredProducts.map((product, i) => (
            <Link
              key={product.id}
              to={product.slug}
              style={{ marginTop: i === 1 ? '32px' : i === 3 ? '48px' : i === 5 ? '24px' : '0' }}
              className="group cursor-pointer"
            >
              {/* Image */}
              <div className="relative overflow-hidden rounded-xl bg-white h-72 mb-5">
                <img
                  src={product.image}
                  alt={`${product.name} - furniture store near me Las Vegas`}
                  title={product.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-600"
                />
                {product.badge && (
                  <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                    product.badge === 'Sale' ? 'bg-dark text-white' :
                    product.badge === 'New Arrival' ? 'bg-gold text-white' :
                    'bg-cream-DEFAULT text-dark'
                  }`}>
                    {product.badge}
                  </div>
                )}
                {/* Quick action */}
                <div className="absolute inset-0 bg-dark/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <span className="bg-cream-DEFAULT text-dark px-5 py-2.5 rounded text-xs font-bold uppercase tracking-wider">
                    View Details
                  </span>
                </div>
              </div>

              {/* Info */}
              <div>
                <span className="text-gold text-xs font-semibold uppercase tracking-[0.15em]">{product.collection}</span>
                <h3 className="font-serif text-xl font-bold text-dark mt-1 mb-2 group-hover:text-gold transition-colors">{product.name}</h3>
                <p className="text-dark/50 text-sm leading-relaxed mb-3">{product.description}</p>
                <div className="flex items-center gap-3">
                  <span className="font-bold text-dark text-lg">{product.price}</span>
                  {product.originalPrice && (
                    <span className="text-dark/30 text-sm line-through">{product.originalPrice}</span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-20 text-center">
          <Link
            to="/collections"
            className="inline-flex items-center gap-3 bg-dark text-white px-10 py-4 rounded text-sm font-semibold uppercase tracking-widest hover:bg-gold transition-colors cursor-pointer whitespace-nowrap"
          >
            Shop All Collections
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
